import React, { useState, forwardRef, useImperativeHandle } from "react";
import {
  Button,
  TextField,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  TablePagination,
} from "@mui/material";
import { FaTrash, FaEdit, FaPlus } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import toast from "react-hot-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchFolders, createFolder, updateFolder, deleteFolder } from "../../api/documentApi";
import Loading from "../Loading";
import DataTableLocation from "../DataTable/DataTableLocation";
import FolderRecordsModal from "./FolderRecordsModal";
import { FaEye } from "react-icons/fa";

const ManageFolders = forwardRef((props, ref) => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [recordsModalOpen, setRecordsModalOpen] = useState(false);
  const [viewingFolder, setViewingFolder] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const { data, isLoading } = useQuery({
    queryKey: ["folders"],
    queryFn: fetchFolders,
  });

  const folders = Array.isArray(data?.data) ? data.data : (data?.data?.folders || []);

  const createMutation = useMutation({
    mutationFn: createFolder,
    onSuccess: (res) => {
      if (res.status) {
        toast.success("Folder created successfully!");
        handleCloseModal();
        queryClient.invalidateQueries(["folders"]);
      } else {
        toast.error(res.message || "Failed to create folder");
      }
    },
    onError: (err) => {
      const errMsg = err.response?.data?.error || err.response?.data?.message || err.message || "An error occurred while creating the folder";
      toast.error(errMsg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateFolder(id, data),
    onSuccess: (res) => {
      if (res.status) {
        toast.success("Folder updated successfully!");
        handleCloseModal();
        queryClient.invalidateQueries(["folders"]);
      } else {
        toast.error(res.message || "Failed to update folder");
      }
    },
    onError: (err) => {
      const errMsg = err.response?.data?.error || err.response?.data?.message || err.message || "An error occurred while updating the folder";
      toast.error(errMsg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFolder,
    onSuccess: (res) => {
      if (res.status) {
        toast.success("Folder deleted successfully!");
        setDeleteModalOpen(false);
        setFolderToDelete(null);
        queryClient.invalidateQueries(["folders"]);
      } else {
        toast.error(res.message || "Failed to delete folder");
      }
    },
    onError: (err) => {
      const errMsg = err.response?.data?.error || err.response?.data?.message || err.message || "An error occurred while deleting the folder";
      toast.error(errMsg);
      setDeleteModalOpen(false);
      setFolderToDelete(null);
    },
  });

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error("Folder name is required");
      return;
    }
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id) => {
    setFolderToDelete(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (folderToDelete) {
      deleteMutation.mutate(folderToDelete);
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setFormData({ name: "", description: "" });
    setModalOpen(true);
  };

  useImperativeHandle(ref, () => ({
    openAddModal
  }));

  const openEditModal = (folder) => {
    setEditingId(folder.id);
    setFormData({ name: folder.name, description: folder.description || "" });
    setModalOpen(true);
  };

  const openRecordsModal = (folder) => {
    setViewingFolder(folder);
    setRecordsModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setFormData({ name: "", description: "" });
  };

  const columns = [
    { 
      field: "name", 
      headerName: "Name", 
      width: 350,
      renderCell: (params) => {
        const desc = params.row.description;
        if (desc) {
          return (
            <Tooltip title={desc} arrow placement="top">
              <span style={{ cursor: "help", borderBottom: "1px dashed #7C2EA0" }}>
                {params.value}
              </span>
            </Tooltip>
          );
        }
        return <span>{params.value}</span>;
      }
    },
    { field: "file_count", headerName: "Files", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => {
        const isDefault = params.row.is_default === 1 || params.row.is_default === true;
        return (
          <>
            <IconButton onClick={() => openRecordsModal(params.row)} color="success" size="small" title="View Files">
              <FaEye size={16} />
            </IconButton>
            {!isDefault && (
              <IconButton onClick={() => openEditModal(params.row)} color="primary" size="small" title="Edit">
                <FaEdit size={16} />
              </IconButton>
            )}
            {!isDefault && (
              <IconButton onClick={() => handleDelete(params.row.id)} color="error" size="small" title="Delete">
                <FaTrash size={16} />
              </IconButton>
            )}
          </>
        );
      },
    },
  ];

  const paginatedFolders = folders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ mt: 2 }}>
      {(isLoading || createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading) && <Loading />}

      <div style={{ marginBottom: "0px", display: "flex", flexDirection: "column" }}>
        <DataTableLocation columns={columns} rows={paginatedFolders} height={400} />
        <TablePagination
          component="div"
          count={folders.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </div>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {editingId ? "Edit Folder" : "Create Folder"}
          <IconButton onClick={handleCloseModal} size="small">
            <IoMdClose />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Folder Name"
              variant="outlined"
              size="small"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description (Optional)"
              variant="outlined"
              size="small"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
            />
            <Button variant="contained" onClick={handleSubmit} sx={{ mt: 1, backgroundColor: "#4A148C" }}>
              {editingId ? "Update" : "Create"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteModalOpen} onClose={() => { setDeleteModalOpen(false); setFolderToDelete(null); }}>
        <DialogTitle>Delete Folder</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this folder? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteModalOpen(false); setFolderToDelete(null); }} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <FolderRecordsModal
        open={recordsModalOpen}
        onClose={() => { setRecordsModalOpen(false); setViewingFolder(null); }}
        folderId={viewingFolder?.id}
        folderName={viewingFolder?.name}
      />
    </Box>
  );
});

export default ManageFolders;
